import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';

import {
  ApiError,
  createProject,
  getCompany,
  listProjects,
  listTemplates,
  type Project,
  type ProjectTemplate,
  type ProjectType,
} from '../api.js';
import { PROJECT_STATUS_LABELS, STAGE_LABELS, TYPE_LABELS } from '../labels.js';
import { navigate } from '../router.js';
import { RegisterToolbar, matches, useRegisterView } from './register-kit.js';

/**
 * Every project this session can see.
 *
 * ON THE REGISTER PATTERN: search, filters, sorts and density, and no row
 * actions — a project is opened, not moved from here. The whole list arrives in
 * one read, so the toolbar's count is the honest one: "showing 4 of 31" means
 * there are 31 and 27 are behind a filter, not behind a page.
 */
interface Props {
  /**
   * `project: create`, asked of the server rather than re-derived here.
   *
   * Both controls below were ungated, so every role that could SEE the list
   * was offered "New project" — a client, a subcontractor, a site engineer.
   * None of them hold the right, so the form would have 403d on save.
   */
  readonly canCreate: boolean;
}

export function Projects({ canCreate }: Props): JSX.Element {
  const [projects, setProjects] = useState<readonly Project[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const view = useRegisterView('newest');

  const refresh = useCallback(async () => {
    try {
      setProjects(await listProjects());
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load projects.');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * The rows on screen.
   *
   * Filtering lives HERE rather than in `useRegisterView`, so every dependency
   * is visible to `react-hooks/exhaustive-deps`. It runs before the early
   * return below because hooks must.
   */
  const shown = useMemo(() => {
    if (!projects) return [];

    const type = view.chosen.type;
    const stage = view.chosen.stage;

    const filtered = projects.filter((project) => {
      // Everything somebody would type: the code they quote, the project's
      // name and the client it is for.
      const hay = [project.code, project.name, project.client_name].filter(Boolean).join(' ');
      if (!matches(hay, view.query)) return false;
      if (type && project.project_type !== type) return false;
      if (stage && project.status !== stage) return false;
      return true;
    });

    const compare: Readonly<Record<string, (a: Project, b: Project) => number>> = {
      // The server's order is newest first, so this is the identity sort and
      // the default. It is named rather than implied, so the box shows it.
      newest: () => 0,
      code: (a, b) => a.code.localeCompare(b.code),
      name: (a, b) => a.name.localeCompare(b.name),
      client: (a, b) => (a.client_name ?? '').localeCompare(b.client_name ?? ''),
    };

    return [...filtered].sort(compare[view.sortKey] ?? compare.newest!);
  }, [projects, view.query, view.chosen.type, view.chosen.stage, view.sortKey]);

  if (showForm) {
    return (
      <NewProjectForm
        onCancel={() => setShowForm(false)}
        // Straight into the new project. The next thing anyone does after
        // creating one is open it; bouncing back to the list makes them find
        // the row they just made.
        onCreated={(id) => navigate(`/projects/${encodeURIComponent(id)}`)}
      />
    );
  }

  return (
    <section>
      <div className="section-head">
        <h2>Projects</h2>
        {canCreate && (
          <button type="button" className="inline" onClick={() => setShowForm(true)}>
            New project
          </button>
        )}
      </div>

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {projects === null && <p aria-live="polite">Loading projects…</p>}

      {/*
        An empty state names the next action ITS READER can take — and when
        they can take none, says why they are looking at nothing.

        Both halves were wrong here. A client signed in, saw "Create the first
        one to set up its lifecycle, team and locations", and a button to do it:
        an instruction written for somebody setting up the company, shown to the
        customer. The second sentence is the one that is actually true for them
        — they are on no project yet, which is a thing somebody else fixes.
      */}
      {projects?.length === 0 && (
        <div className="empty">
          <p>
            <strong>No projects yet.</strong>
          </p>
          {canCreate ? (
            <>
              <p>Create the first one to set up its lifecycle, team and locations.</p>
              <button type="button" className="inline" onClick={() => setShowForm(true)}>
                Create the first project
              </button>
            </>
          ) : (
            <p>
              You will see a project here once somebody adds you to one. Ask whoever is running
              the job.
            </p>
          )}
        </div>
      )}

      {projects && projects.length > 0 && (
        <RegisterToolbar
          view={view}
          searchLabel="Search projects"
          searchPlaceholder="Code, name, client"
          shown={shown.length}
          total={projects.length}
          noun={['project', 'projects']}
          filters={[
            {
              key: 'type',
              label: 'Type',
              options: Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label })),
            },
            {
              key: 'stage',
              label: 'Stage',
              options: Object.entries(PROJECT_STATUS_LABELS).map(([value, label]) => ({
                value,
                label,
              })),
            },
          ]}
          sorts={[
            { key: 'newest', label: 'Newest first' },
            { key: 'code', label: 'Code' },
            { key: 'name', label: 'Name' },
            { key: 'client', label: 'Client' },
          ]}
        />
      )}

      {projects && projects.length > 0 && shown.length === 0 && (
        /*
         * A DIFFERENT empty state from "no projects yet": this reader has
         * projects and a filter that excludes all of them.
         */
        <div className="empty">
          <p>
            <strong>No project matches that.</strong>
          </p>
          <p>
            <button type="button" className="link-button" onClick={view.clear}>
              Clear the search and filters
            </button>{' '}
            to see all {projects.length}.
          </p>
        </div>
      )}

      {shown.length > 0 && (
        <table className="grid rk-cards">
          <caption className="visually-hidden">
            Projects you have access to. {shown.length} of {projects?.length ?? 0} shown.
          </caption>
          <thead>
            <tr>
              <th scope="col">Code</th>
              <th scope="col">Project</th>
              <th scope="col">Client</th>
              <th scope="col">Type</th>
              <th scope="col">Stage</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((project) => (
              <tr key={project.id}>
                <td className="mono" data-label="Code">{project.code}</td>
                <td data-label="Project">
                  {/*
                    A real link, not a clickable row: it can be tabbed to,
                    opened in a new tab, and read out by a screen reader as
                    what it is.
                  */}
                  <a
                    href={`/projects/${encodeURIComponent(project.id)}`}
                    onClick={(event) => {
                      if (event.metaKey || event.ctrlKey || event.shiftKey) return;
                      event.preventDefault();
                      navigate(`/projects/${encodeURIComponent(project.id)}`);
                    }}
                  >
                    {project.name}
                  </a>
                </td>
                <td data-label="Client">{project.client_name ?? '—'}</td>
                <td data-label="Type">{TYPE_LABELS[project.project_type] ?? project.project_type}</td>
                <td data-label="Stage">{PROJECT_STATUS_LABELS[project.status] ?? project.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {projects && projects.length > 0 && (
        // The COUNT moved into the toolbar, which says "showing 4 of 31" and
        // offers a way back to all of them. What is left here is the sentence
        // that count cannot say: why the number is what it is.
        <p className="note">
          Projects belonging to other companies are not just hidden here — they are invisible to
          this session at the database level.
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

interface FormProps {
  readonly onCancel: () => void;
  readonly onCreated: (projectId: string) => void | Promise<void>;
}

function NewProjectForm({ onCancel, onCreated }: FormProps): JSX.Element {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [clientName, setClientName] = useState('');
  const [projectType, setProjectType] = useState<ProjectType>('office');
  const [startDate, setStartDate] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [contractValue, setContractValue] = useState('');
  /**
   * The company's currency, which the new project will be created in.
   *
   * Not sent — the server takes it from the company, so a label is the only
   * thing this is for. But it has to be RIGHT: a field labelled AED on a
   * company that works in SAR is a field somebody types the wrong number into,
   * and the number is the contract value.
   *
   * 'AED' until the answer arrives, and only for the moment it takes.
   */
  const [currency, setCurrency] = useState('AED');
  const [templates, setTemplates] = useState<readonly ProjectTemplate[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // A Project Manager may not hold `company:view`, and that must not turn a
    // label into an error message — the fallback is already correct enough.
    void getCompany()
      .then((company) => setCurrency(company.default_currency))
      .catch(() => undefined);
    // Same posture for templates: a role without `template:view` just gets no
    // dropdown, which is the truthful screen for them.
    void listTemplates()
      .then((view) => setTemplates(view.project.filter((t) => t.is_active)))
      .catch(() => undefined);
  }, []);

  // Digits and at most four decimals — the same shape the API accepts, checked
  // here so a typo is caught before a round trip rather than after one.
  const money = contractValue.replace(/,/g, '').trim();
  const moneyValid = money === '' || /^[0-9]+(\.[0-9]{1,4})?$/.test(money);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!moneyValid) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createProject({
        code: code.trim(),
        name: name.trim(),
        project_type: projectType,
        ...(clientName.trim() ? { client_name: clientName.trim() } : {}),
        ...(startDate ? { start_date: startDate } : {}),
        ...(targetDate ? { target_completion_date: targetDate } : {}),
        // Sent as a STRING, all the way to numeric(18,4). A JSON number would
        // round-trip through a float on the way (07 §DI2).
        ...(money ? { contract_value: money } : {}),
        ...(templateId ? { template_id: templateId } : {}),
      });
      await onCreated(created.id);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not create the project.');
      setBusy(false);
    }
  }

  return (
    <section>
      <div className="section-head">
        <h2>New project</h2>
      </div>

      <form onSubmit={onSubmit} noValidate>
        {error && (
          <div className="alert" role="alert">
            <span>{error}</span>
          </div>
        )}

        <div className="field">
          <label htmlFor="code">Project code</label>
          <input
            id="code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            maxLength={40}
            disabled={busy}
            placeholder="PR112"
          />
          <p className="hint">A short reference, unique within your company.</p>
        </div>

        <div className="field">
          <label htmlFor="name">Project name</label>
          <input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={200}
            disabled={busy}
          />
        </div>

        <div className="field">
          <label htmlFor="client">Client</label>
          <input
            id="client"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            maxLength={200}
            disabled={busy}
          />
        </div>

        <div className="field">
          <label htmlFor="type">Project type</label>
          <select
            id="type"
            value={projectType}
            onChange={(e) => setProjectType(e.target.value as ProjectType)}
            disabled={busy}
          >
            {Object.entries(TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {templates.length > 0 && (
          <div className="field">
            <label htmlFor="template">Start from a template</label>
            <select
              id="template"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              disabled={busy}
            >
              <option value="">No template — start empty</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name} ({template.milestones.length} milestones)
                </option>
              ))}
            </select>
            <p className="hint">
              {templateId
                ? 'The template’s milestones are copied in, dated from the start date — so the start date is required.'
                : 'A template copies a starting milestone set into the new project.'}
            </p>
          </div>
        )}

        <div className="field-row">
          <div className="field">
            <label htmlFor="start">Start date</label>
            <input
              id="start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="field">
            <label htmlFor="target">Target completion</label>
            <input
              id="target"
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              disabled={busy}
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="contract">Contract value ({currency})</label>
          <input
            id="contract"
            value={contractValue}
            onChange={(e) => setContractValue(e.target.value)}
            inputMode="decimal"
            disabled={busy}
            aria-invalid={!moneyValid}
            aria-describedby="contract-hint"
            placeholder="12340000"
          />
          <p className="hint" id="contract-hint">
            {moneyValid
              ? 'Optional. Only people with financial access on this project will see it.'
              : 'Numbers only, with up to four decimal places.'}
          </p>
        </div>

        <div className="button-row">
          <button
            type="submit"
            disabled={
              busy || !code.trim() || !name.trim() || !moneyValid ||
              // A template anchors its milestones on the start date.
              Boolean(templateId && !startDate)
            }
          >
            {busy ? 'Creating…' : 'Create project'}
          </button>
          <button type="button" className="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </div>

        <p className="hint">
          Creating a project also sets up its eight lifecycle stages —{' '}
          {Object.values(STAGE_LABELS).join(', ')} — and adds you to the team.
        </p>
      </form>
    </section>
  );
}
